import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";

// Event window — splash is shown to first-time visitors until this cutoff.
// After Aug 23, 2026 23:59 ET the splash never shows again.
const EVENT_END_ISO = "2026-08-23T23:59:00-04:00";
const SESSION_KEY = "cbb-anyc-splash-seen";

export default function AnimeNYCSplash() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Don't show on the dedicated Anime NYC page (they already opted in) or auth callback.
    if (location.pathname === "/anime-nyc" || location.pathname.startsWith("/auth")) return;
    // Only show if we're still before the event cutoff.
    const now = new Date();
    const cutoff = new Date(EVENT_END_ISO);
    if (Number.isNaN(cutoff.getTime()) || now > cutoff) return;
    // Show once per browser session.
    try {
      if (sessionStorage.getItem(SESSION_KEY) === "1") return;
    } catch { /* Safari private mode — still show */ }
    // Small delay so it doesn't feel like a page-load hijack.
    const t = setTimeout(() => setOpen(true), 500);
    return () => clearTimeout(t);
    // Only evaluate on first mount for this session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch { /* no-op */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/85 backdrop-blur-sm animate-[fadeIn_0.4s_ease-out]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="anyc-splash-title"
      data-testid="anyc-splash-modal"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-[520px] max-h-[95vh] bg-[#0a0a0a] border border-[#C5A059]/30 shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close event announcement"
          data-testid="anyc-splash-close"
          className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center bg-black/70 border border-white/20 hover:bg-black hover:border-[#C5A059] transition-colors"
        >
          <X size={16} className="text-white" />
        </button>

        {/* Poster */}
        <div className="relative bg-black">
          <img
            src="/art/anyc_splash.png"
            alt="Chains Beneath Bakhtak — Anime NYC Aug 20-23, 2026"
            id="anyc-splash-title"
            className="w-full h-auto block select-none"
            draggable={false}
          />
        </div>

        {/* CTA bar */}
        <div className="p-4 sm:p-5 bg-[#0a0a0a] border-t border-[#C5A059]/20 grid grid-cols-2 gap-3">
          <Link
            to="/login"
            onClick={dismiss}
            data-testid="anyc-splash-signup"
            className="inline-flex items-center justify-center bg-[#C5A059] text-black px-4 py-3 text-[11px] sm:text-xs tracking-[0.22em] uppercase font-semibold hover:bg-[#D4AF68] transition-colors"
          >
            Sign Up
          </Link>
          <Link
            to="/nation-quiz"
            onClick={dismiss}
            data-testid="anyc-splash-quiz"
            className="inline-flex items-center justify-center border border-[#C5A059]/50 text-[#EAE0D5] px-4 py-3 text-[11px] sm:text-xs tracking-[0.22em] uppercase hover:bg-[#C5A059]/10 hover:border-[#C5A059] transition-colors"
          >
            Take the Quiz
          </Link>
        </div>
      </div>
    </div>
  );
}
